import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSocket } from "@/context/SocketContext";
import { useCall } from "@/context/CallContext";
import GiftMenu from "@/components/GiftMenu";
import PrivateConfirmModal from "@/components/PrivateConfirmModal";
import { GIFT_ICON, dollars } from "@/lib/giftIcons";
import { isPaid } from "@/lib/membership";
import { Send, Video, PhoneMissed, Gift, Lock, Camera, Clock, Eye, Flame, Sparkles, ArrowLeft } from "lucide-react";
import { FoundingBadge } from "@/components/FoundingBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { ProfileNudgeBanner } from "@/components/ProfileNudgeBanner";
import { TrialBanner } from "@/components/TrialBanner";
import { toast } from "sonner";
import { useDefocusHidden, useScreenshotKey } from "@/lib/screenshotGuard";
import { Watermark } from "@/components/Watermark";
import { READ_EVENT } from "@/lib/unread";

const VANISH_OPTIONS = [5, 10, 30, 60];

function EphemeralBubble({ m, mine, viewerName }) {
  const [phase, setPhase] = useState(m.meta?.destroyed ? "gone" : "locked");
  const [img, setImg] = useState(null);
  const [left, setLeft] = useState(0);
  const hidden = useDefocusHidden();

  // Auto-vanish the instant the tab is hidden / window blurs (anti-screenshot).
  useEffect(() => {
    if (hidden && phase === "viewing") { setPhase("gone"); setImg(null); }
  }, [hidden, phase]);

  useScreenshotKey(() => setPhase((p) => {
    if (p === "viewing") { setImg(null); toast("Screenshots aren't allowed — photo hidden"); return "gone"; }
    return p;
  }));

  useEffect(() => {
    if (phase !== "viewing") return;
    if (left <= 0) { setPhase("gone"); setImg(null); return; }
    const t = setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, left]);

  const open = async () => {
    try {
      const { data } = await api.post(`/messages/${m.id}/reveal`);
      setImg(data.data_url);
      setLeft(data.timer);
      setPhase("viewing");
    } catch (err) {
      setPhase("gone");
      toast.error(err.response?.status === 410 ? "This photo has already vanished" : "Could not open photo");
    }
  };

  if (mine) {
    return (
      <div data-testid={`ephemeral-${m.id}`} className="ml-auto max-w-[75%] flex items-center gap-2 px-4 py-3 rounded-2xl rounded-br-sm bg-wine/40 border border-wine/50 text-sm">
        <Flame size={15} className="text-gold" /> Disappearing photo · {m.meta?.timer || ""}s
      </div>
    );
  }
  if (phase === "gone") {
    return (
      <div data-testid={`ephemeral-${m.id}`} className="max-w-[75%] flex items-center gap-2 px-4 py-3 rounded-2xl rounded-bl-sm bg-white/5 border border-white/10 text-sm text-text-muted italic">
        <Flame size={14} /> Photo vanished
      </div>
    );
  }
  if (phase === "viewing" && img) {
    return (
      <div data-testid={`ephemeral-view-${m.id}`} className="max-w-[75%] relative rounded-2xl overflow-hidden border border-gold/40">
        <img src={img} alt="disappearing" className="w-full max-h-80 object-contain bg-black" />
        <Watermark text={viewerName} />
        <span data-testid="ephemeral-countdown" className="absolute top-2 right-2 flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] text-gold bg-black/70 px-3 py-1 rounded-full">
          <Clock size={12} /> {left}s
        </span>
        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-white/70 bg-black/60 px-2 py-0.5 rounded-full">
          <Lock size={10} /> Screenshots not allowed
        </span>
      </div>
    );
  }
  return (
    <button data-testid={`ephemeral-open-${m.id}`} onClick={open}
      className="max-w-[75%] flex items-center gap-3 px-5 py-4 rounded-2xl rounded-bl-sm bg-gradient-to-br from-wine/30 to-black border border-gold/40 text-sm hover:border-gold transition-colors">
      <span className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center"><Eye size={18} className="text-gold" /></span>
      <span className="text-left">
        <span className="block text-gold">Tap to view</span>
        <span className="block text-[11px] text-text-muted">Self-destructs in {m.meta?.timer || 10}s</span>
      </span>
    </button>
  );
}


const FALLBACK = "https://images.unsplash.com/photo-1637059880830-59a90102de77";

export default function Messages() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addMessageListener, addReadListener, addTypingListener, send: wsSend } = useSocket();
  const { startCall } = useCall();
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [peerTyping, setPeerTyping] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [privOpen, setPrivOpen] = useState(false);
  const [photoMenu, setPhotoMenu] = useState(false);
  const photoTimer = useRef(10);
  const photoRef = useRef(null);
  const bottom = useRef(null);
  const clearTypingTimer = useRef(null);

  const pickTimer = (s) => { photoTimer.current = s; setPhotoMenu(false); photoRef.current?.click(); };
  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !id) return;
    const fd = new FormData();
    fd.append("to_user_id", id);
    fd.append("timer", String(photoTimer.current));
    fd.append("file", file);
    try {
      await api.post("/messages/photo", fd);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send photo");
    }
  };

  const loadConvos = () => api.get("/messages/conversations").then(({ data }) => setConvos(data));
  useEffect(() => { loadConvos(); }, []);

  const markRead = (otherId) => api.post(`/messages/${otherId}/read`).then(() => window.dispatchEvent(new Event(READ_EVENT))).catch(() => {});

  useEffect(() => {
    if (!id) { setThread([]); setOther(null); return; }
    api.get(`/profiles/${id}`).then(({ data }) => setOther(data));
    api.get(`/messages/${id}`).then(({ data }) => { setThread(data); markRead(id); });
  }, [id]);

  // Real-time: append messages pushed over the socket
  useEffect(() => {
    const off = addMessageListener((m) => {
      const involvesThread = id && (m.from_user === id || m.to_user === id);
      if (involvesThread) {
        setThread((t) => (t.some((x) => x.id === m.id) ? t : [...t, m]));
        if (m.from_user === id) markRead(id);
      }
      loadConvos();
    });
    return off;
  }, [id, addMessageListener]);

  // Read receipts: when the other person reads my messages, mark them seen
  useEffect(() => {
    const off = addReadListener((data) => {
      if (id && data.reader === id) {
        setThread((t) => t.map((m) => (m.from_user === user?.id ? { ...m, read: true } : m)));
      }
    });
    return off;
  }, [id, user, addReadListener]);

  // Typing indicator from the other person (auto-clears after 3s)
  useEffect(() => { setPeerTyping(false); }, [id]);
  useEffect(() => {
    const off = addTypingListener((data) => {
      if (id && data.from === id) {
        setPeerTyping(data.typing);
        if (clearTypingTimer.current) clearTimeout(clearTypingTimer.current);
        if (data.typing) clearTypingTimer.current = setTimeout(() => setPeerTyping(false), 3500);
      }
    });
    return off;
  }, [id, addTypingListener]);

  const onType = (e) => {
    setText(e.target.value);
    if (id) wsSend({ type: "typing", to: id, typing: true });
  };

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text;
    setText("");
    if (id) wsSend({ type: "typing", to: id, typing: false });
    try {
      await api.post("/messages", { to_user_id: id, body });
    } catch (err) {
      setText(body);
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail, {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/premium") },
        });
      } else {
        toast.error(err.response?.data?.detail || "Could not send message");
      }
    }
  };

  return (
    <div className={`pt-20 h-[100dvh] max-w-6xl mx-auto px-3 sm:px-6 flex flex-col ${id ? "" : "pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0"}`}>
      <h1 className={`font-display text-3xl sm:text-4xl font-light py-4 sm:py-6 ${id ? "hidden md:block" : ""}`}>Messages</h1>
      <div className={id ? "hidden md:block" : ""}>
        <TrialBanner />
        <ProfileNudgeBanner />
      </div>
      <div className="flex-1 grid md:grid-cols-3 gap-6 overflow-hidden pb-2 sm:pb-6 min-h-0">
        <div className={`border border-white/8 rounded-2xl overflow-y-auto bg-surface ${id ? "hidden md:block" : ""}`}>
          {convos.length === 0 && <p className="p-6 text-sm text-text-muted">No conversations yet. Start one from a profile.</p>}
          {convos.map((c) => (
            <Link key={c.user.id} to={`/messages/${c.user.id}`} data-testid={`convo-${c.user.id}`}
              className={`flex items-center gap-3 p-4 border-b border-white/5 transition-colors ${id === c.user.id ? "bg-white/5" : "hover:bg-white/5"}`}>
              <div className="relative shrink-0">
                <img src={c.user.photo_url || FALLBACK} alt="" className={`w-11 h-11 rounded-full object-cover ${(c.user.discreet_mode || !isPaid(user)) ? "blur-md" : ""}`} />
                {c.user.online && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-surface" />}
              </div>
              <div className="min-w-0"><p className="text-sm truncate flex items-center gap-1.5">{c.user.display_name} <VerifiedBadge user={c.user} size="xs" testId={`conv-id-verified-${c.user.id}`} /> <FoundingBadge user={c.user} testId={`conv-founding-${c.user.id}`} /></p><p className="text-xs text-text-muted truncate">{c.last_message}</p></div>
            </Link>
          ))}
        </div>

        <div className={`md:col-span-2 border border-white/8 rounded-2xl flex-col bg-surface overflow-hidden min-h-0 ${id ? "flex" : "hidden md:flex"}`}>
          {!id ? (
            <div className="flex-1 flex items-center justify-center text-text-muted font-display italic text-2xl">Select a conversation</div>
          ) : (
            <>
              {other && (
                <div className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-white/8">
                  <Link to="/messages" data-testid="thread-back" aria-label="Back to conversations"
                    className="md:hidden w-10 h-10 -ml-1 flex items-center justify-center rounded-full text-text-muted active:bg-white/10 shrink-0">
                    <ArrowLeft size={20} />
                  </Link>
                  <Link to={`/profile/${other.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="relative">
                      <img src={other.photo_url || FALLBACK} alt="" className={`w-9 h-9 rounded-full object-cover ${(other.discreet_mode || !isPaid(user)) ? "blur-md" : ""}`} />
                      {other.online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-surface" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm leading-none flex items-center gap-1.5 truncate">{other.display_name} <VerifiedBadge user={other} size="xs" testId="thread-id-verified" /> <FoundingBadge user={other} testId="thread-founding-badge" /></p>
                      {other.online && <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 mt-1">Online now</p>}
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {other.private_available && other.private_rate >= 299 && (
                      <button data-testid="start-private" onClick={() => setPrivOpen(true)} aria-label="Start private session"
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] h-10 px-3 sm:px-4 rounded-full border border-wine/50 text-gold hover:bg-wine/10 transition-colors">
                        <Lock size={14} /> <span className="hidden sm:inline">Private · {dollars(other.private_rate)}/min</span>
                      </button>
                    )}
                    <button data-testid="start-call" onClick={() => startCall(other)} aria-label="Start video call"
                      className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] h-10 px-3 sm:px-4 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors">
                      <Video size={16} /> <span className="hidden sm:inline">Go live</span>
                    </button>
                  </div>
                </div>
              )}
              <PrivateConfirmModal open={privOpen} onClose={() => setPrivOpen(false)} user={other} />
              <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5 space-y-3">
                {thread.map((m) => {
                  if (m.kind === "missed_call") {
                    return (
                      <div key={m.id} data-testid={`missed-call-${m.id}`} className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] text-text-muted py-2">
                        <PhoneMissed size={13} className="text-red-400" /> {m.from_user === user?.id ? "You called — no answer" : "Missed video date"}
                      </div>
                    );
                  }
                  const mine = m.from_user === user?.id;
                  if (m.kind === "system") {
                    return (
                      <div key={m.id} data-testid={`system-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[90%] sm:max-w-[80%] rounded-2xl border border-gold/40 bg-gold/8 px-4 sm:px-5 py-3.5">
                          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gold mb-1.5"><Sparkles size={11} /> {m.meta?.label || "Velluto Admin"}</p>
                          <p className="text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</p>
                        </div>
                      </div>
                    );
                  }
                  if (m.kind === "private_summary" && m.meta) {
                    return (
                      <div key={m.id} data-testid={`private-receipt-${m.id}`} className="flex justify-center py-1">
                        <div className="rounded-2xl border border-wine/40 bg-wine/10 px-7 py-4 text-center">
                          <p className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold"><Lock size={12} /> Private session</p>
                          <p className="text-sm mt-2">
                            {m.meta.minutes} minute{m.meta.minutes > 1 ? "s" : ""} · {mine
                              ? <span className="text-gold">{dollars(m.meta.total)} spent</span>
                              : <span className="text-gold">{dollars(m.meta.credit)} earned</span>}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  if (m.kind === "gift" && m.meta) {
                    const GI = GIFT_ICON[m.meta.icon] || GIFT_ICON.heart;
                    return (
                      <div key={m.id} data-testid={`gift-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className="flex flex-col items-center gap-2 rounded-2xl border px-6 py-4" style={{ borderColor: `${m.meta.color}66`, background: `${m.meta.color}14` }}>
                          <span className="gift-pop w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${m.meta.color}22` }}>
                            <GI size={24} style={{ color: m.meta.color }} strokeWidth={1.5} />
                          </span>
                          <span className="text-xs uppercase tracking-[0.15em]" style={{ color: m.meta.color }}>{m.meta.gift_name} · {dollars(m.meta.amount)}</span>
                          <span className="text-[11px] text-text-muted">{mine ? "You sent a gift" : "sent you a gift"}</span>
                        </div>
                      </div>
                    );
                  }
                  if (m.kind === "ephemeral_photo") {
                    return (
                      <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <EphemeralBubble m={m} mine={mine} viewerName={user?.display_name || "Member"} />
                      </motion.div>
                    );
                  }
                  return (
                    <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className={`max-w-[85%] sm:max-w-[75%] px-4 py-2.5 rounded-2xl text-[15px] sm:text-sm break-words ${mine ? "ml-auto bg-wine rounded-br-sm" : "bg-white/8 rounded-bl-sm"}`}>
                      {m.body}
                    </motion.div>
                  );
                })}
                {(() => {
                  const mine = thread.filter((m) => m.from_user === user?.id && m.kind !== "missed_call");
                  const last = mine[mine.length - 1];
                  return last && last.read ? (
                    <p data-testid="seen-indicator" className="text-[10px] uppercase tracking-[0.2em] text-text-muted text-right pr-1">Seen</p>
                  ) : null;
                })()}
                {peerTyping && (
                  <div data-testid="typing-indicator" className="flex items-center gap-1.5 bg-white/8 rounded-2xl rounded-bl-sm w-fit px-4 py-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                )}
                <div ref={bottom} />
              </div>
              <form onSubmit={send} className="p-2.5 sm:p-4 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-white/8 flex gap-2 sm:gap-3 items-center">
                <button type="button" data-testid="open-gift-menu" onClick={() => setGiftOpen(true)} aria-label="Send a gift"
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold/10 transition-colors shrink-0">
                  <Gift size={17} />
                </button>
                <div className="relative shrink-0">
                  <input ref={photoRef} data-testid="ephemeral-file" type="file" accept="image/*" className="hidden" onChange={onPhotoPicked} />
                  <button type="button" data-testid="ephemeral-photo-btn" onClick={() => setPhotoMenu((v) => !v)} aria-label="Send a disappearing photo"
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-wine/50 text-gold flex items-center justify-center hover:bg-wine/10 transition-colors">
                    <Camera size={17} />
                  </button>
                  {photoMenu && (
                    <div data-testid="ephemeral-timer-menu" className="absolute bottom-14 left-0 z-20 bg-surface border border-white/15 rounded-2xl p-2 shadow-2xl w-44">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-text-muted px-2 py-1.5 flex items-center gap-1"><Flame size={11} className="text-gold" /> Disappears after</p>
                      {VANISH_OPTIONS.map((s) => (
                        <button key={s} data-testid={`ephemeral-timer-${s}`} onClick={() => pickTimer(s)}
                          className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-xl hover:bg-white/5 transition-colors">
                          <Clock size={13} className="text-gold" /> {s} seconds
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input data-testid="message-input" value={text} onChange={onType} placeholder="Write something intriguing…" enterKeyHint="send"
                  className="flex-1 min-w-0 bg-transparent border-b border-white/15 py-2 text-base sm:text-sm focus:border-wine outline-none transition-colors" />
                <button data-testid="message-send" aria-label="Send message" className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-wine hover:bg-wine-hover transition-colors flex items-center justify-center shrink-0"><Send size={16} /></button>
              </form>
              <GiftMenu open={giftOpen} onClose={() => setGiftOpen(false)} toUserId={id} toName={other?.display_name} context="message" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
